import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import './PuzzleBoard.css';

/**
 * PuzzleBoard-Komponente für das Puzzle-Spielfeld
 * 
 * @param {Object} props - Die Komponenten-Properties
 * @param {string} props.imageSrc - Pfad zum Puzzle-Bild
 * @param {string} props.difficulty - Schwierigkeitsgrad (easy, medium, hard)
 * @param {boolean} props.rotationEnabled - Ob Rotation der Teile aktiviert ist
 * @param {Function} props.onComplete - Callback, wenn Puzzle gelöst ist
 * @param {Function} props.onReturn - Callback für Rückkehr zum Menü
 */
const PuzzleBoard = ({ imageSrc, difficulty, rotationEnabled, onComplete, onReturn }) => {
  // State für Puzzle-Teile und Spielstatus
  const [pieces, setPieces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [gameTime, setGameTime] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  
  // Joker-bezogener State
  const [jokerCount, setJokerCount] = useState({
    easy: 1,
    medium: 2, 
    hard: 3
  }[difficulty]);
  const [isJokerActive, setIsJokerActive] = useState(false);
  const [jokerTimeLeft, setJokerTimeLeft] = useState(0);
  const jokerTimerRef = useRef(null);
  
  // Referenzen
  const boardRef = useRef(null);
  const timerRef = useRef(null);
  
  // Konfiguration basierend auf Schwierigkeitsgrad
  const gridSize = {
    easy: 3,     // 3x3 = 9 Teile
    medium: 4,   // 4x4 = 16 Teile
    hard: 5      // 5x5 = 25 Teile
  }[difficulty];
  
  // Bild in Puzzle-Teile zerlegen
  useEffect(() => {
    setIsLoading(true);
    
    const img = new Image();
    img.onload = () => {
      createPuzzlePieces(img);
      setIsLoading(false);
      setGameStarted(true);
    };
    img.src = imageSrc;
    
    // Timer starten
    timerRef.current = setInterval(() => {
      setGameTime(prevTime => prevTime + 1);
    }, 1000);
    
    // Cleanup beim Unmount
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [imageSrc, difficulty]);
  
  // Puzzle-Teile erstellen (mit Rotation-Erweiterung)
  const createPuzzlePieces = (img) => {
    // Zielgröße für das quadratische Puzzle-Board
    const targetSize = 600;
    
    // Bestimme die Größe des quadratischen Ausschnitts aus dem Originalbild
    const cropSize = Math.min(img.width, img.height);
    
    // Berechne die Position des quadratischen Ausschnitts (zentriert)
    const cropX = (img.width - cropSize) / 2;
    const cropY = (img.height - cropSize) / 2;
    
    // Berechne die Größe jedes Puzzle-Teils
    const pieceSize = Math.floor(targetSize / gridSize);
    
    // Erstelle ein Haupt-Canvas für das quadratische, zugeschnittene Bild
    const mainCanvas = document.createElement('canvas');
    const mainCtx = mainCanvas.getContext('2d');
    mainCanvas.width = pieceSize * gridSize; // Exakte Größe für das Raster
    mainCanvas.height = pieceSize * gridSize;
    
    // Zeichne den quadratischen Ausschnitt des Bildes auf das Haupt-Canvas
    mainCtx.drawImage(
      img, 
      cropX, cropY, cropSize, cropSize, // Quellenkoordinaten (zentrierter Ausschnitt)
      0, 0, mainCanvas.width, mainCanvas.height // Zielkoordinaten (gesamtes Canvas)
    );
    
    // Pieces-Canvas für einzelne Teile
    const pieceCanvas = document.createElement('canvas');
    const pieceCtx = pieceCanvas.getContext('2d');
    pieceCanvas.width = pieceSize;
    pieceCanvas.height = pieceSize;
    
    const newPieces = [];
    
    // Für jedes Puzzle-Teil
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        // Position im zugeschnittenen Bild
        const sx = col * pieceSize;
        const sy = row * pieceSize;
        
        // Canvas löschen und Teil aus dem Haupt-Canvas extrahieren
        pieceCtx.clearRect(0, 0, pieceSize, pieceSize);
        pieceCtx.drawImage(
          mainCanvas, 
          sx, sy, pieceSize, pieceSize, 
          0, 0, pieceSize, pieceSize
        );
        
        // In Dataurl umwandeln
        const pieceDataUrl = pieceCanvas.toDataURL('image/png');
        
        // Zufällige Rotation, wenn Rotation aktiviert ist
        const rotation = rotationEnabled ? 
          [0, 90, 180, 270][Math.floor(Math.random() * 4)] : 
          0;
        
        // Puzzle-Teil-Objekt erstellen
        newPieces.push({
          id: `piece_${row}_${col}`,
          src: pieceDataUrl,
          currentPosition: { row, col },
          originalPosition: { row, col },
          rotation: rotation,
          correctRotation: 0 // Die korrekte Rotation ist immer 0
        });
      }
    }
    
    // Teile mischen
    const shuffledPieces = [...newPieces];
    for (let i = shuffledPieces.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * i);
      [shuffledPieces[i].currentPosition, shuffledPieces[j].currentPosition] = 
      [shuffledPieces[j].currentPosition, shuffledPieces[i].currentPosition];
    }
    
    setPieces(shuffledPieces);
  };
  
  // Puzzle-Teile tauschen
  const swapPieces = (piece1Index, piece2Index) => {
    const newPieces = [...pieces];
    const tempPosition = { ...newPieces[piece1Index].currentPosition };
    
    newPieces[piece1Index].currentPosition = { ...newPieces[piece2Index].currentPosition };
    newPieces[piece2Index].currentPosition = tempPosition;
    
    setPieces(newPieces);
    
    // Prüfen, ob Puzzle vollständig ist
    checkCompletion(newPieces);
  };
  
  // Feuerwerk-Effekt abspielen
  const playFireworks = () => {
    const duration = 3000;
    const end = Date.now() + duration;
    
    const interval = setInterval(() => {
      if (Date.now() > end) {
        return clearInterval(interval);
      }
      
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#FF0000', '#FFD700', '#00FF00', '#0000FF', '#FF00FF']
      });
    }, 250);
  };
  
  // Prüfen, ob das Puzzle vollständig ist (mit Rotation-Check)
  const checkCompletion = (currentPieces) => {
    const isCompleted = currentPieces.every(piece => 
      piece.currentPosition.row === piece.originalPosition.row && 
      piece.currentPosition.col === piece.originalPosition.col && 
      (!rotationEnabled || piece.rotation === piece.correctRotation)
    );
    
    if (isCompleted && !isComplete) {
      setIsComplete(true);
      if (timerRef.current) clearInterval(timerRef.current);
      
      // Zeige zuerst das vollständige Bild
      setShowFullImage(true);
      
      // Starte den Feuerwerk-Effekt
      playFireworks();
      
      // Zeige die Glückwunschnachricht nach einer Verzögerung
      setTimeout(() => {
        setShowCompletionMessage(true);
      }, 2000);
      
      if (onComplete) onComplete(gameTime);
    }
  };
  
  // Formatierung der Spielzeit
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Drag & Drop-Funktionalität
  const [draggedPiece, setDraggedPiece] = useState(null);
  
  const handleDragStart = (e, index) => {
    setDraggedPiece(index);
    e.dataTransfer.effectAllowed = 'move';
    // Transparentes Bild für Drag-Icon
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedPiece !== null && draggedPiece !== index) {
      swapPieces(draggedPiece, index);
      setDraggedPiece(null);
    }
  };
  
  // Joker aktivieren
  const activateJoker = () => {
    if (jokerCount > 0 && !isJokerActive) {
      setJokerCount(prev => prev - 1);
      setIsJokerActive(true);
      setJokerTimeLeft(8); // 8 Sekunden
      
      // Timer für Joker starten
      jokerTimerRef.current = setInterval(() => {
        setJokerTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(jokerTimerRef.current);
            setIsJokerActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };
  
  // Cleanup für Joker-Timer
  useEffect(() => {
    return () => {
      if (jokerTimerRef.current) {
        clearInterval(jokerTimerRef.current);
      }
    };
  }, []);
  
  // Puzzle-Teil drehen
  const rotatePiece = (index) => {
    if (!rotationEnabled) return;
    
    const newPieces = [...pieces];
    const currentRotation = newPieces[index].rotation;
    newPieces[index].rotation = (currentRotation + 90) % 360;
    
    setPieces(newPieces);
    checkCompletion(newPieces);
  };
  
  // Wenn das Puzzle geladen wird
  if (isLoading) {
    return (
      <div className="puzzle-loading">
        <div className="loading-spinner"></div>
        <p>Puzzle wird vorbereitet...</p>
      </div>
    );
  }
  
  return (
    <div className="puzzle-game">
      <div className="game-info">
        <div className="timer">Zeit: {formatTime(gameTime)}</div>
        <div className="joker-area">
          <button 
            className={`joker-button ${jokerCount === 0 ? 'disabled' : ''}`}
            onClick={activateJoker}
            disabled={jokerCount === 0 || isJokerActive}
          >
            Joker ({jokerCount})
          </button>
          {isJokerActive && (
            <div className="joker-timer">
              Lösungsbild für: {jokerTimeLeft}s
            </div>
          )}
        </div>
        <button className="return-button" onClick={onReturn}>
          Zurück zum Menü
        </button>
      </div>
      
      {(showFullImage && !showCompletionMessage) || isJokerActive ? (
        <div className={`full-image-container ${isJokerActive ? 'joker-active' : ''}`}>
          <img 
            src={imageSrc} 
            alt="Vollständiges Puzzle" 
            className="full-image"
          />
          
          {isJokerActive && rotationEnabled && (
            <div 
              className="rotation-overlay"
              style={{
                gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                gridTemplateRows: `repeat(${gridSize}, 1fr)`
              }}
            >
              <div className="rotation-hint-text">
                Falsch gedrehte Teile sind markiert
              </div>
              
              {pieces.map((piece, index) => {
                const { row, col } = piece.currentPosition;
                const isIncorrectRotation = piece.rotation !== piece.correctRotation;
                
                return (
                  <div
                    key={`hint-${piece.id}`}
                    className={`rotation-hint-cell ${isIncorrectRotation ? 'incorrect' : ''}`}
                    style={{
                      gridRow: row + 1,
                      gridColumn: col + 1
                    }}
                  >
                    {isIncorrectRotation && (
                      <>
                        <div className="rotation-arrow"></div>
                        <div className="rotation-value">{piece.rotation}°</div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div 
          className="puzzle-board"
          ref={boardRef}
          style={{
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            gridTemplateRows: `repeat(${gridSize}, 1fr)`
          }}
        >
          {pieces.map((piece, index) => {
            // Position im Grid basierend auf aktueller Position
            const { row, col } = piece.currentPosition;
            
            return (
              <div
                key={piece.id}
                className={`puzzle-piece ${draggedPiece === index ? 'dragging' : ''}`}
                style={{
                  gridRow: row + 1,
                  gridColumn: col + 1,
                  transform: `rotate(${piece.rotation}deg)`
                }}
                draggable="true"
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  rotatePiece(index);
                }}
              >
                <img src={piece.src} alt={`Puzzle Teil ${index}`} />
                {rotationEnabled && isJokerActive && (
                  <div className="rotation-indicator">
                    <span className="rotation-value">{piece.rotation}°</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {showCompletionMessage && (
        <div className="completion-message">
          <h2>Glückwunsch!</h2>
          <p>Du hast das Puzzle in {formatTime(gameTime)} gelöst!</p>
          <button onClick={onReturn}>Zurück zur Auswahl</button>
        </div>
      )}
    </div>
  );
};

export default PuzzleBoard; 